import { Pressable, AppState, View, Linking, Platform, ViewProps, StyleSheet } from "react-native";
import { Image } from 'expo-image';
import styles from "@stylesheet";
import { t } from "i18next";
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import React, { useEffect, useState, useMemo } from 'react';
import * as Animatable from 'react-native-animatable';
import parseByDelimiter from "@utils/parseByDelimiter";
import {  uniqueId } from "lodash";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import Button from "@components/UI/Button/Button";
import toTitleCase from "@hooks/toTitleCase";
import apis, { updateDataItemHistory } from "@api/api";
import UserIcon from "@assets/icons/UserIcon";
import {  SizableText } from "tamagui";
import { useDeviceType } from "@hooks/useDeviceType";

let BackgroundTimer: any = null;
if (Platform.OS !== 'web') {
  BackgroundTimer = require('react-native-background-timer');
}

interface DefaultCustomerProps {
  item: any;
  navigation?: any;
  schema?: any;
  callback?: Function;
  removeInteraction?: boolean;
  maxWidth?: number;
  style?: ViewProps;
}

const DefaultCustomer = ({ style, maxWidth, removeInteraction = false, item, navigation, schema, callback }: DefaultCustomerProps) => {
  const { data } = item;
  const [initialArray, setInitialArray] = useState([]);
  const theme = themeSelector();
  const [maximize, setMaximize] = useState(false);
  const { isTablet } = useDeviceType();
  const tablet = isTablet;
  const [containerWidth, setContainerWidth] = useState(0);
  const widthReq = containerWidth > 200 ? true : false;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const appState = React.useRef(AppState.currentState);
  useEffect(() => {
    if (Platform.OS === 'web') return;
    
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
        // App came back to foreground
        if (callStartTime) {
          const duration = Math.floor((Date.now() - callStartTime) / 1000);
          setCallDuration(duration);
          console.log(`Call duration: ${duration} seconds`);

          // Save call duration in history
          updateDataItemHistory(item._id, { type: 'call', duration });

          // Clear the timer
          BackgroundTimer.stopBackgroundTimer();
        }
        setCallStartTime(null);
      }
      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [callStartTime]);


    useEffect(() => {
      if (data) {
        setInitialArray(getFirstThreeKeyValuePairs(data));
      } else {
        setInitialArray([]);
      }
    }, [item, data]);

    const getFirstThreeKeyValuePairs = (obj) => {
      if (!obj) {
        return [[schema[0].title, 'No Data'], [schema[1].title, 'No Data'], [schema[2].title, 'No Data']];
      }


      const objLowercase = Object.keys(obj).reduce((acc, key) => {
        if (typeof obj[key] !== 'object') {
          acc[key.toLowerCase()] = obj[key];
        }
        return acc;
      }, {});

      // Prioritize fields with 'name' in them first
      const nameFields = Object.keys(objLowercase).filter(key => key.includes('name'));
      const otherFields = Object.keys(objLowercase).filter(key => !key.includes('name'));
      
      // Sort: name fields first, then priority properties, then others
      const priorityProperties = ["firstname", "surname", "email", "company", "company name", "companyname"];
      const result = [];

      // Add name fields first
      for (const property of nameFields) {
        if (objLowercase.hasOwnProperty(property)) {
          result.push([property, objLowercase[property]]);
        }
      }

      // Add other priority properties
      for (const property of priorityProperties) {
        if (objLowercase.hasOwnProperty(property) && !result.find(([prop]) => prop === property)) {
          result.push([property, objLowercase[property]]);
        }
      }

      // Add remaining fields
      for (const [key, value] of Object.entries(objLowercase)) {
        if (!result.find(([prop]) => prop === key)) {
          result.push([key, value]);
        }
      }

      if (result?.length != schema?.length) {
        //Pad with empty data from schema
        for (let i = result?.length; i < schema?.length; i++) {
          result.push([schema[i].title, 'No Data']);
        }
      }

      return result;
    };

    // Check if this is a POI import (company/business)
    const isPOIImport = item?.enrichmentMetadata?.source === 'networking_map' || 
                        item?.enrichmentMetadata?.poi_type || 
                        (data && (data.business_name || data.company_name || data.display_name));

    const renderImage = () => {
      if (item.image?.length) {
        return (
          <View style={modernStyles.imageContainer}>
            <Image 
              contentFit='cover' 
              source={{ uri: item.image }} 
              style={modernStyles.profileImage}
            />
          </View>
        );
      } else if (isPOIImport) {
        // Show company icon for POI imports instead of user icon
        return (
          <View 
            style={[modernStyles.avatarContainer, { backgroundColor: theme ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.2)' }]}
          >
            <MaterialIcons 
              name="business" 
              size={Platform.select({ web: 50, default: 40 })} 
              color={theme ? '#8b5cf6' : '#a78bfa'} 
            />
          </View>
        );
      } else {
        return (
          <View 
            style={[modernStyles.avatarContainer, { backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(99, 102, 241, 0.2)' }]}
          >
            <UserIcon 
              height={Platform.select({ web: 70, default: 50 })} 
              width={Platform.select({ web: 70, default: 50 })} 
            />
          </View>
        );
      }
    };

    return useMemo(() => (
      <View 
        onLayout={({ nativeEvent }) => setContainerWidth(nativeEvent.layout.width)} 
        style={{ paddingHorizontal: 20, backgroundColor: theme ? "white" : "black", padding: 15, borderRadius: 20 }}
        // style={[
        //   modernStyles.mainContainer,
        //   {
        //     backgroundColor: theme 
        //       ? 'rgba(255, 255, 255, 0.95)' 
        //       : 'rgba(30, 41, 59, 0.95)',
        //     maxHeight: maximize 
        //       ? 600 
        //       : (containerWidth < 200 ? (Platform.OS != 'web' ? 200 : 250) : (Platform.OS != 'web' ? 270 : 350)),
        //     width: tablet ? "95%" : (Platform.OS === 'web' ? "98%" : "100%"),
        //     maxWidth: maxWidth ? maxWidth : undefined,
        //   },
        //   style
        // ]} 
      >
        <View style={[
          modernStyles.contentContainer,
          {
            flexDirection: containerWidth < 200 ? 'column' : 'row',
          }
        ]}>
          <Pressable 
            onPress={() => { if (callback) callback() }} 
            style={[
              modernStyles.imageSection,
              { alignSelf: containerWidth < 200 ? 'center' : 'flex-start' }
            ]}
          >
            {renderImage()}
          </Pressable>
          
          <View style={modernStyles.infoSection}>
            {initialArray.map((data, index) => {
              if (index < 3) return (
                <View 
                  key={uniqueId(data[1])} 
                  style={modernStyles.infoRow}
                >
                  {containerWidth > 200 && (
                    <SizableText style={[
                      modernStyles.fieldLabel,
                      { color: theme ? '#64748b' : '#94a3b8' }
                    ]}>
                      {parseByDelimiter(data[0])}
                    </SizableText>
                  )}
                  <SizableText 
                    numberOfLines={2} 
                    style={[
                      modernStyles.fieldValue,
                      {
                        color: theme ? '#1e293b' : '#f1f5f9',
                        textAlign: containerWidth < 200 ? "center" : "left",
                        fontStyle: data[1] == "No Data" ? 'italic' : 'normal',
                        opacity: data[1] == "No Data" ? 0.6 : 1
                      }
                    ]}
                  >
                    {['name', 'surname', 'Surname', 'title'].includes(data[0]) ? toTitleCase(data[1]) : data[1]}
                  </SizableText>
                </View>
              )
            })}
          </View>
        </View>
        {
          !removeInteraction &&
          <View 
            style={modernStyles.actionContainer}
          >
            <View style={modernStyles.actionButtonsRow}>
              <View style={modernStyles.quickActionsSection}>
                <Pressable 
                  disabled={!data?.location} 
                  style={[
                    modernStyles.actionButton, 
                    { 
                      backgroundColor: data?.location 
                        ? (theme ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.2)') 
                        : (theme ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.2)'),
                      borderColor: data?.location ? '#22c55e' : '#94a3b8'
                    }
                  ]} 
                  onPress={() => console.log('do something with location')}
                >
                  <MaterialIcons 
                    name="location-on" 
                    size={20} 
                    color={data?.location ? '#22c55e' : '#94a3b8'} 
                  />
                </Pressable>
                
                <Pressable 
                  disabled={!data?.phone && !data?.number && !data?.telephone} 
                  style={[
                    modernStyles.actionButton,
                    { 
                      backgroundColor: (data?.phone || data?.number || data?.telephone)
                        ? (theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)') 
                        : (theme ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.2)'),
                      borderColor: (data?.phone || data?.number || data?.telephone) ? '#3b82f6' : '#94a3b8'
                    }
                  ]} 
                  onPress={async () => {
                    Linking.openURL(`tel:${data?.phone || data?.number || data?.telephone}`)
                    setCallStartTime(Date.now());
                    BackgroundTimer.runBackgroundTimer(() => {
                      console.log('This will be executed every 1 seconds');
                    }, 1000);
                  }}
                >
                  <MaterialIcons 
                    name="phone" 
                    size={20} 
                    color={(data?.phone || data?.number || data?.telephone) ? '#3b82f6' : '#94a3b8'} 
                  />
                </Pressable>
                
                <Pressable 
                  disabled={!data?.email} 
                  style={[
                    modernStyles.actionButton,
                    { 
                      backgroundColor: data?.email 
                        ? (theme ? 'rgba(245, 101, 101, 0.1)' : 'rgba(245, 101, 101, 0.2)') 
                        : (theme ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.2)'),
                      borderColor: data?.email ? '#f56565' : '#94a3b8'
                    }
                  ]} 
                  onPress={() => Linking.openURL(`mailto:${data?.email}`)}
                >
                  <MaterialIcons 
                    name="email" 
                    size={20} 
                    color={data?.email ? '#f56565' : '#94a3b8'} 
                  />
                </Pressable>
              </View>
              
              <View style={modernStyles.primaryActionSection}>
                <Button 
                  textColor="white" 
                    icon="arrow-right" 
                  inverse 
                  buttonText={`${t('dataStoreFindDataRecentlyAccessedDataOpenButton1')}`} 
                  iconStyle={{ left: 0 }} 
                  iconColor="white" 
                  onPress={async () => { 
                    await apis.addRecent(item._id, 'data'); 
                    navigation.push('TimeLineData', { dataId: item._id, params: { data: item, schema: schema } }) 
                  }} 
                  customStyle={{
                    ...modernStyles.primaryButton,
                    backgroundColor: theme ? '#3b82f6' : '#4f46e5',
                  }} 
                />
              </View>
            </View>
          </View>
        }
      </View>
    ), [data, initialArray, theme, containerWidth]);
  };

// Modern styling system
const modernStyles = StyleSheet.create({
  mainContainer: {
    margin: 16,
    marginHorizontal: 20,
    padding: 20,
    alignSelf: 'stretch',
    borderRadius: 24,
    overflow: 'visible',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    }),
  },
  contentContainer: {
    gap: 16,
    alignItems: 'stretch',
    marginBottom: 16,
  },
  imageSection: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  imageContainer: {
    borderRadius: 20,
    overflow: 'hidden',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
  },
  profileImage: {
    width: Platform.select({ web: 120, default: 100 }),
    height: Platform.select({ web: 120, default: 100 }),
    borderRadius: 20,
  },
  avatarContainer: {
    width: Platform.select({ web: 120, default: 100 }),
    height: Platform.select({ web: 120, default: 100 }),
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  infoSection: {
    flex: 1,
    gap: 12,
  },
  infoRow: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.02)',
    marginVertical: 2,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldValue: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  actionContainer: {
    marginTop: 8,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  actionButtonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  quickActionsSection: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  actionButton: {
    width: 44,
    height: 44,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(8px)',
      transition: 'all 0.2s ease',
    }),
  },
  primaryActionSection: {
    flex: 1,
    marginLeft: 8,
  },
  primaryButton: {
    borderRadius: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    minHeight: 48,
    elevation: 4,
    shadowColor: '#3b82f6',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
  },
});

export default DefaultCustomer;
